import * as React from "react";
import { lazy, Suspense } from "react";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Navigate, Outlet, useParams } from "react-router-dom";
import ProtectedRoute from "../../components/protectedRoute";
// layouts
import MainLayout from "../../layouts/dashboard";
import MSLayout from "../../layouts/ms-layout";
import { NotFoundRoute } from "./NotFoundRoute";
// ----------------------------------------------------------------------
import { HatLoading } from "../../components/loading-screen";
import { BannerDisplay } from "../../components/BannerDisplay";
import { PosthogFeatureFlag, FeatureFlag } from "../../components/FeatureFlag";
import { PaletteDebug } from "../../pages/debug/palette-debug";
import { HlDebug } from "../../pages/debug/hldebug";
import { FeatureFlags } from "../../pages/debug/feature-flags";
import { AwardsDebug } from "../../pages/debug/AwardsDebug";
import { FacetsDebug } from "../../pages/debug/FacetsDebug";
import { ResetUserPage } from "../../pages/debug/ResetUserPage";
import { NextStepsDebug } from "../../pages/debug/NextStepsDebug";
import { HatsDebug } from "../../pages/debug/Hats-Debug";
import { PDFPreviewPage } from "../../pages/debug/pdf-preview";
import { CoursePlanPDFPreviewPage } from "../../pages/debug/course-plan-pdf-preview";
import { ClimbPlanPDFPreviewPage } from "../../pages/debug/climb-plan-pdf-preview";
import { CertificatesDebug } from "../../pages/debug/CertificatesDebug";
import { CelebrationsDebug } from "../../pages/debug/CelebrationsDebug";
import { QaProvisionUserPage } from "../../pages/debug/qa-provision/QaProvisionUserPage";
import { QaManifestReferencePage } from "../../pages/debug/qa-provision/QaManifestReferencePage";
import {
  LadderCompletionDebug,
  isLadderCompletionDebugAllowed,
} from "../../pages/debug/LadderCompletionDebug";
import { IbmSync } from "../../pages/ibm-sync/IbmSync";

const WfUsageActivityGuard = () => {
  const currentUser = useCurrentUser();
  const role = currentUser?.role ?? "";
  const schoolId = currentUser?.uniqueIdentifiers?.find(
    (u) => u.schoolID,
  )?.schoolID;
  if (role === "teacher" && schoolId) {
    return (
      <Navigate
        to={`/reports/wf-usage-activity-report/site/${schoolId}`}
        replace
      />
    );
  }
  return <UsageActivityReport />;
};

const RedirectToClimberDetail = () => {
  const { climberId } = useParams();
  return <Navigate to={`/users/climber/${climberId}`} replace />;
};

const BrowseLadders = lazy(() => import("../../pages/ladders"));
const Login = lazy(() => import("../../pages/login"));
const PostLogin = lazy(() => import("../../pages/post-login"));

const ClimberDashboard = lazy(() => import("../../pages/climber-dashboard"));
const MSClimberDashboard = lazy(
  () => import("../../pages/middle-school/ms-climber-dashboard"),
);
const MSClimberDashboardCheckin = lazy(
  () =>
    import("../../pages/middle-school/ms-climber-dashboard-checkin/index.tsx"),
);
const MSClimberProfile = lazy(
  () => import("../../pages/middle-school/ms-climber-profile/MSProfilePage"),
);
const MSClimberProfileBadges = lazy(
  () => import("../../pages/middle-school/ms-climber-profile-badges"),
);
const ClustersAndPathways = lazy(
  () => import("../../pages/middle-school/ms-clusters-and-pathways"),
);
const MSCareerCoursePlanner = lazy(
  () => import("../../pages/middle-school/ms-career-course-planner/index.tsx"),
);
const MSCoursePlanner = lazy(
  () => import("../../pages/middle-school/ms-course-planner/index.tsx"),
);
const MSCoursePlanReview = lazy(
  () => import("../../pages/middle-school/ms-course-plan-review/index.tsx"),
);
const MSSchoolPlans = lazy(
  () => import("../../pages/middle-school/ms-school-plans/index.tsx"),
);
const MSShoutOuts = lazy(
  () => import("../../pages/middle-school/ms-shout-outs/index.tsx"),
);
const MSClimberShoutOuts = lazy(
  () => import("../../pages/middle-school/ms-climber-shout-outs/index.tsx"),
);
const TeacherMSClimberExperienceSetup = lazy(
  () =>
    import(
      "../../pages/middle-school/ms-teacher-climber-experience-setup/index.tsx"
    ),
);
const Cluster = lazy(() => import("../../pages/middle-school/ms-cluster"));
const Pathway = lazy(() => import("../../pages/middle-school/ms-pathway"));
const TeacherDashboard = lazy(() => import("../../pages/teacher-dashboard"));
const WorksitesPage = lazy(() => import("../../pages/worksites"));
const SuperAdminDashboard = lazy(
  () => import("../../pages/super-admin-dashboard"),
);
const DistrictAdminDashboard = lazy(
  () => import("../../pages/district-admin-dashboard"),
);

const SchoolStaffDashboard = lazy(
  () => import("../../pages/school-staff-dashboard"),
);

const Achievements = lazy(() => import("../../pages/achievements"));

const Sites = lazy(() => import("../../pages/sites"));
const SchoolGroups = lazy(() => import("../../pages/school-groups"));

const ManageCoaches = lazy(() => import("../../pages/manage-coaches"));
const ManageStaff = lazy(() => import("../../pages/manage-staff"));
const ManageAdmins = lazy(() => import("../../pages/manage-admins"));
const ClimberSearch = lazy(() => import("../../pages/climber-search"));
const RosterUsers = lazy(() => import("../../pages/roster-users"));
const ClimberDetail = lazy(() => import("../../pages/climber-detail"));
const Settings = lazy(() => import("../../pages/district-settings"));
const CoursePlanConfig = lazy(() => import("../../pages/course-plan-config"));

const Courses = lazy(() => import("../../pages/courses"));
const Portfolio = lazy(() => import("../../pages/portfolio"));
const HatsMain = lazy(() => import("../../pages/hats"));
const MSHats = lazy(() => import("../../pages/middle-school/ms-hats"));
const GroupReport = lazy(() => import("../../pages/reports"));
const CareerClimbs = lazy(
  () => import("../../pages/career-climbs/climberCareerClimbPage"),
);
const ClimberList = lazy(() => import("../../pages/reports/climber-list"));
const ClimberCareerClimbItem = lazy(
  () =>
    import("../../pages/career-climbs/components/climber-career-climb-item"),
);
const CoachCareerClimbs = lazy(
  () => import("../../pages/career-climbs/coachCareerClimbPage"),
);
const CoachCareerClimbItem = lazy(
  () => import("../../pages/career-climbs/components/coach-career-climb-item"),
);
const Onboarding = lazy(() => import("../../pages/onboarding"));

const ReportsDashboard = lazy(
  () => import("../../pages/reports/report-dashboard"),
);
const PlanInsightsPage = lazy(
  () => import("../../pages/reports/plan-insights"),
);
const ClimbPlanInsightsPage = lazy(
  () => import("../../pages/reports/climb-plan-insights"),
);

const LadderIframe = lazy(() => import("../../pages/ladders/ladderIframe"));
const Wizard = lazy(() => import("../../pages/middle-school/ms-wizard"));
const MSDiscoverCore = lazy(
  () => import("../../pages/middle-school/ms-discover-core"),
);
const MSProfileOnboardV2 = lazy(
  () => import("../../pages/middle-school/ms-profile-onboard-v2"),
);
const CareerClimbReport = lazy(
  () => import("../../pages/reports/career-climb-report"),
);
const CoachGroups = lazy(() => import("../../pages/coach-groups"));
const CoachGroup = lazy(() => import("../../pages/reports"));
const LadderActivity = lazy(() => import("../../pages/ladders/ladderActivity"));
const PathwayContentPage = lazy(
  () => import("../../pages/middle-school/ms-pathway/PathwayContentPage"),
);
const EducatorDownloads = lazy(() => import("../../pages/educator-downloads"));
const StudentAnswerReview = lazy(
  () => import("../../pages/student-answer-review"),
);
const Planner = lazy(() => import("../../pages/planner"));
const StaffCareerPlanPage = lazy(() => import("../../pages/staff-career-plan"));
const StaffClimbPlanPage = lazy(() => import("../../pages/staff-climb-plan"));
const PlanAdmin = lazy(() => import("../../pages/plan-admin"));
const WorksiteVisitProgressPage = lazy(
  () => import("../../pages/reports/worksite-visit-progress/index.tsx"),
);
const WorksiteVisitProgressTeacherDetailPage = lazy(
  () =>
    import(
      "../../pages/reports/worksite-visit-progress/teacher-detail/index.tsx"
    ),
);
const WorksiteVisitProgressSchoolDetailPage = lazy(
  () =>
    import(
      "../../pages/reports/worksite-visit-progress/school-detail/index.tsx"
    ),
);
const PathwayInterestReport = lazy(
  () => import("../../pages/reports/pathway-interest-report/index.tsx"),
);
const CertificateReport = lazy(() =>
  import("../../pages/reports/certificate-report/CertificateReport").then(
    (m) => ({ default: m.CertificateReport }),
  ),
);
const CertificateClassView = lazy(() =>
  import("../../pages/reports/certificate-report/ClassView").then((m) => ({
    default: m.ClassView,
  })),
);
const ClusterProgressReport = lazy(
  () => import("../../pages/reports/cluster-progress-report/index.tsx"),
);
const UsageActivityReport = lazy(
  () => import("../../pages/reports/wf-usage-activity-report/index.tsx"),
);
const UsageActivitySiteDetail = lazy(
  () =>
    import(
      "../../pages/reports/wf-usage-activity-report/site-detail/index.tsx"
    ),
);
const MsUsageActivityReport = lazy(
  () => import("../../pages/reports/ms-usage-report/index.tsx"),
);
const MsSchoolDetail = lazy(
  () => import("../../pages/reports/ms-usage-report/school-detail/index.tsx"),
);
const LadderTimeReport = lazy(
  () => import("../../pages/reports/ladder-time-report/index.tsx"),
);
const HatsStaffDashboard = lazy(
  () => import("../../pages/hats-staff/index.tsx"),
);
const HatsStaffDistrictHealth = lazy(
  () => import("../../pages/hats-staff/DistrictHealthView.tsx"),
);
const HatsStaffDistrictBrowser = lazy(
  () => import("../../pages/hats-staff/DistrictBrowserView.tsx"),
);
const HatsStaffDistrictDetail = lazy(
  () => import("../../pages/hats-staff/DistrictDetailView.tsx"),
);
const HatsStaffDistrictForm = lazy(
  () => import("../../pages/hats-staff/DistrictFormView.tsx"),
);
const HatsStaffDistrictSessions = lazy(
  () => import("../../pages/hats-staff/DistrictSessionsView.tsx"),
);
const HatsStaffDistrictSchools = lazy(
  () => import("../../pages/hats-staff/DistrictSchoolsView.tsx"),
);
const HatsStaffSchoolSections = lazy(
  () => import("../../pages/hats-staff/SchoolSectionsView.tsx"),
);
const HatsStaffDistrictPeople = lazy(
  () => import("../../pages/hats-staff/DistrictPeopleView.tsx"),
);
const HatsStaffAdminDetail = lazy(
  () => import("../../pages/hats-staff/DistrictAdminDetailView.tsx"),
);
const ExplorationReport = lazy(() =>
  import("../../pages/reports/exploration-report/ExplorationReport").then(
    (m) => ({ default: m.ExplorationReport }),
  ),
);
const ExplorationSchoolView = lazy(() =>
  import("../../pages/reports/exploration-report/SchoolView").then((m) => ({
    default: m.ExplorationSchoolView,
  })),
);
const ExplorationReportLayout = lazy(() =>
  import("../../pages/reports/exploration-report/ExplorationReportLayout").then(
    (m) => ({ default: m.ExplorationReportLayout }),
  ),
);
const MsProgramMilestones = lazy(() =>
  import("../../pages/reports/ms-program-milestones/MsProgramMilestones").then(
    (m) => ({ default: m.MsProgramMilestones }),
  ),
);

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    // -------- onboarding for Climbers ----------
    element: (
      <ProtectedRoute requiredRole={["climber"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "onboarding",
        element: <Outlet />,
        children: [{ element: <Onboarding />, index: true }],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "ms", "wf"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladder-iframe/:id",
        element: <Outlet />,
        children: [{ element: <LadderIframe />, index: true }],
      },
    ],
  },

  // -------- Ladders & Achievements for Climbers ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders",
        element: <Outlet />,
        children: [{ element: <BrowseLadders />, index: true }],
      },
      {
        path: "achievements",
        element: <Outlet />,
        children: [{ element: <Achievements />, index: true }],
      },
    ],
  },
  // -------- Full screen content for Climbers ----------
  {
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
    ],
  },
  // -------- Hats & Ladders internal staff only ----------
  // "hatsstaff" is the lowercased Cognito group, which is how useCurrentUser
  // derives role. The group is assigned by the postAuth trigger from the `hnl-`
  // id prefix, so a user only reaches this route by holding that record.
  {
    element: (
      <ProtectedRoute requiredRole={["hatsstaff"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "hatsstaff",
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: "dashboard", element: <HatsStaffDashboard /> },
          { path: "health", element: <HatsStaffDistrictHealth /> },
          { path: "districts", element: <HatsStaffDistrictBrowser /> },
          {
            path: "districts/new",
            element: (
              <ProtectedRoute requireWrite>
                <HatsStaffDistrictForm />
              </ProtectedRoute>
            ),
          },
          {
            path: "districts/:districtId",
            element: <HatsStaffDistrictDetail />,
          },
          {
            path: "districts/:districtId/edit",
            element: (
              <ProtectedRoute requireWrite>
                <HatsStaffDistrictForm />
              </ProtectedRoute>
            ),
          },
          {
            path: "districts/:districtId/sessions",
            element: <HatsStaffDistrictSessions />,
          },
          {
            path: "districts/:districtId/people",
            element: <HatsStaffDistrictPeople />,
          },
          {
            path: "districts/:districtId/schools",
            element: <HatsStaffDistrictSchools />,
          },
          {
            path: "districts/:districtId/schools/:schoolId/sections",
            element: <HatsStaffSchoolSections />,
          },
          // The people pages, under the staff prefix rather than the shared
          // /users/* routes. Same components — there is one implementation of
          // "manage coaches", and a staff copy would drift from it — but
          // reached through the /hatsstaff guard, so they are unambiguously
          // staff surfaces and the sidebar stays in the staff area instead of
          // flipping to the district-admin nav mid-journey.
          { path: "users/coaches", element: <ManageCoaches /> },
          { path: "users/staff", element: <ManageStaff /> },
          { path: "users/admins", element: <ManageAdmins /> },
          {
            path: "users/admins/:adminId",
            element: <HatsStaffAdminDetail />,
          },
          { path: "users/climbers", element: <ClimberSearch /> },
          { path: "users/climber/:climberId", element: <ClimberDetail /> },
          // The district admin's class page, kept inside the staff nav.
          { path: "classes/:id", element: <CoachGroup /> },
          {
            path: "classes/:id/career-climb/:climbID",
            element: <CareerClimbReport />,
          },
        ],
      },
    ],
  },
  // -------- Full screen content for Staff (Teachers, School Staff, District Admins) ----------
  {
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
      >
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "climber-experience/ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
    ],
  },
  {
    // --------- Courses for Climbers ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          {
            path: "/courses/career-climbs/:id",
            element: <ClimberCareerClimbItem />,
          },
        ],
      },
    ],
  },
  {
    // -------- Hats for climber ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "hats/*",
        element: <HatsMain />,
      },
    ],
  },
  {
    // -------- CareerClimbs for climber ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout sx={{ p: "0 !important" }}>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "career-climbs",
        element: <Outlet />,
        children: [
          { element: <CareerClimbs />, index: true },
          {
            path: "/career-climbs/:id",
            element: <ClimberCareerClimbItem />,
          },
        ],
      },
    ],
  },
  {
    // -------- Climber Dashboard ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "/climber/dashboard",
        children: [
          {
            element: (
              <ProtectedRoute
                requiredRole={["climber"]}
                requiredLevel={["hs", "wf"]}
              >
                <ClimberDashboard />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
    ],
  },
  // -------- MS Climbers ----------

  {
    element: (
      <ProtectedRoute
        requiredRole={["climber", "teacher", "schoolstaff", "districtadmin"]}
        requiredLevel={["ms"]}
      >
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ms/climber/climb/:id",
        element: (
          <MSLayout backLabel="Back">
            <ClimberCareerClimbItem breadcrumb={false} middleSchool={true} />
          </MSLayout>
        ),
      },
      {
        path: "ms/climber/discover-core",
        element: (
          <MSLayout backLabel="Back">
            <MSDiscoverCore />
          </MSLayout>
        ),
      },
      // Planner
      {
        path: "ms/planner/*",
        element: <Planner />,
      },

      {
        path: "ms/climber/cluster/:clusterId",
        element: <Cluster />,
      },
      {
        path: "ms/climber/pathway/:pathwayId",
        element: <Pathway />,
      },

      {
        path: "ms/climber/dashboard",
        element: (
          <>
            <BannerDisplay />
            <MSClimberDashboard />
          </>
        ),
      },
      {
        path: "ms/climber/dashboard/checkin",
        element: <MSClimberDashboardCheckin />,
      },
      {
        path: "ms/climber/profile",
        element: <MSClimberProfile />,
      },
      {
        path: "ms/climber/profile/badges",
        element: <MSClimberProfileBadges />,
      },
      {
        path: "ms/climber/shout-outs",
        element: <MSClimberShoutOuts />,
      },
      {
        path: "ms/climber/cluster-pathway",
        element: <ClustersAndPathways />,
      },
      {
        path: "ms/climber/pathway/:pathwayId/content/:contentId",
        element: <PathwayContentPage />,
      },
      {
        path: "ms/climber/wizard",
        element: <Wizard />,
      },
      {
        path: "ms/climber/onboard/profile",
        element: <MSProfileOnboardV2 />,
      },
      {
        path: "ms/hats/*",
        element: <MSHats />,
      },
    ],
  },

  {
    element: (
      <ProtectedRoute
        requiredRole={["schoolstaff", "districtadmin"]}
        requiredLevel={["ms"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "staff/career-course-planner/:teacherId",
        element: <MSCareerCoursePlanner />,
      },
    ],
  },
  // -------- MS Coaches ---------

  {
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
        requiredLevel={["ms"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/teacher/ms-climber-experience",
        element: <TeacherMSClimberExperienceSetup />,
      },
      {
        path: "staff/career-course-planner",
        element: <MSCareerCoursePlanner />,
      },
      {
        path: "staff/school-plans",
        element: <MSSchoolPlans />,
      },
      {
        path: "staff/shout-outs",
        element: <MSShoutOuts />,
      },
    ],
  },

  // -------- MS School Staff section removed - they use unified non-MS routes

  {
    // -------- class section ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "/section/:id",
        children: [
          {
            element: (
              <ProtectedRoute
                requiredRole={["schoolstaff", "districtadmin"]}
                requiredLevel={["ms", "hs"]}
              >
                <GroupReport />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
    ],
  },

  // -------- MS Admins (reuse unified dashboard) ----------
  {
    element: (
      <ProtectedRoute requiredRole={["districtadmin"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // ms/districtadmin/dashboard removed in favor of unified district admin dashboard
      {
        path: "settings",
        children: [
          {
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]} requireWrite>
                <Settings />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
      {
        path: "settings/courses",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]} requireWrite>
            <CoursePlanConfig />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // -------- Ladders for Climbers ----------
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders",
        element: <Outlet />,
        children: [{ element: <BrowseLadders />, index: true }],
      },
      {
        path: "achievements",
        element: <Outlet />,
        children: [{ element: <Achievements />, index: true }],
      },
    ],
  },
  {
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "riasec",
        element: <Navigate to="/hldebug/riasec" />,
      },
      {
        path: "palette",
        element: <Navigate to="/hldebug/palette" />,
      },
      {
        path: "hldebug",
        element: <Outlet />,
        children: [
          { element: <HlDebug />, index: true },
          { path: "facets", element: <FacetsDebug /> },
          { path: "awards", element: <AwardsDebug /> },
          { path: "next-steps", element: <NextStepsDebug /> },
          { path: "ms-hats-country", element: <HatsDebug /> },
          { path: "palette", element: <PaletteDebug /> },
          { path: "flags", element: <FeatureFlags /> },
          { path: "reset-user", element: <ResetUserPage /> },
          { path: "pdf-preview", element: <PDFPreviewPage /> },
          {
            path: "course-plan-pdf-preview",
            element: <CoursePlanPDFPreviewPage />,
          },
          {
            path: "climb-plan-pdf-preview",
            element: <ClimbPlanPDFPreviewPage />,
          },
          { path: "certificates", element: <CertificatesDebug /> },
          { path: "celebrations", element: <CelebrationsDebug /> },
          { path: "provision-user", element: <QaProvisionUserPage /> },
          {
            path: "provision-user/manifest",
            element: <QaManifestReferencePage />,
          },
          // Driven by `pnpm plan-pdf-export`. The env var is set only by that
          // CLI on the build it drives. The dynamic import lives INSIDE the
          // branch on purpose: hoisting it to a module-level `lazy` binding
          // makes Rollup emit the chunk even when the route folds away, so the
          // harness would still ship. Here it drops entirely. The page itself
          // also refuses to run anywhere but localhost.
          ...(import.meta.env.VITE_PLAN_EXPORT_HARNESS === "1"
            ? (() => {
                const PlanExportHarness = lazy(
                  () => import("../../pages/debug/plan-export"),
                );
                return [
                  { path: "plan-export/*", element: <PlanExportHarness /> },
                ];
              })()
            : []),
          ...(isLadderCompletionDebugAllowed()
            ? [
                {
                  path: "ladder-completion",
                  element: <LadderCompletionDebug />,
                },
              ]
            : []),
          { path: "rustici", element: <Navigate to="/ibm-sync" /> },
        ],
      },
    ],
  },
  {
    // -------- Full screen content for Climbers ----------
    element: (
      <ProtectedRoute>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
      { path: "ibm-sync", element: <IbmSync /> },
    ],
  },

  // -------- Climber Routes (HS/WF) ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Courses
      {
        path: "courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          { path: "career-climbs/:id", element: <ClimberCareerClimbItem /> },
        ],
      },
      // Hats
      { path: "hats/*", element: <HatsMain /> },
      // Portfolio
      { path: "portfolio", element: <Portfolio /> },
    ],
  },

  // -------- Career Climbs for Climbers (special layout) ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout sx={{ p: "0 !important" }}>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "career-climbs",
        element: <Outlet />,
        children: [
          { element: <CareerClimbs />, index: true },
          { path: ":id", element: <ClimberCareerClimbItem /> },
        ],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Reports
      {
        path: "reports",
        element: <Outlet />,
        children: [{ path: "plan-admin", element: <PlanAdmin /> }], // This is not available to teachers.
      },
    ],
  },

  {
    // -------- Plan review ----------
    // Outside Staff Routes so Hats staff reach them from a climber's profile
    // without the district admin view. Reviewing is gated in the pages.
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      // Career Plan
      {
        path: "teacher/career-plan/:studentId/:careerPlanId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
              "hatsstaff",
            ]}
          >
            <StaffCareerPlanPage />
          </ProtectedRoute>
        ),
      },
      // Course Plan
      {
        path: "teacher/course-plan/:climberId/:coursePlanId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
              "hatsstaff",
            ]}
          >
            <PosthogFeatureFlag flag="ms-course-plan">
              <MSCoursePlanReview />
            </PosthogFeatureFlag>
          </ProtectedRoute>
        ),
      },
      // Climb Plan - the educator's review. Reading is access-checked in the
      // lambda, so the role list here is the outer gate, not the only one.
      {
        path: "teacher/climb-plan/:climberId/:climbPlanId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
              "hatsstaff",
            ]}
          >
            <StaffClimbPlanPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // -------- Staff Routes ----------
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Reports
      {
        path: "reports",
        element: <Outlet />,
        children: [
          { path: "dashboard", element: <ReportsDashboard />, index: true },
          { path: "plan-insights", element: <PlanInsightsPage /> },
          {
            path: "climb-plan-insights",
            element: <ClimbPlanInsightsPage />,
          },
          { path: "plan-admin", element: <PlanAdmin /> },
          { path: "pathway-interests", element: <PathwayInterestReport /> },
          {
            path: "cluster-progress-report",
            element: <ClusterProgressReport />,
          },
          {
            path: "worksite-visit-progress",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "worksite-visit-progress/school/:schoolId",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressSchoolDetailPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "worksite-visit-progress/:teacherId",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressTeacherDetailPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "certificate-completion",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <CertificateReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "certificate-completion/:sectionId",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <CertificateClassView />
              </ProtectedRoute>
            ),
          },
          {
            path: "wf-usage-activity-report",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <WfUsageActivityGuard />
              </ProtectedRoute>
            ),
          },
          {
            path: "wf-usage-activity-report/site/:siteId",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <UsageActivitySiteDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "ms-usage-report",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <MsUsageActivityReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "ms-usage-report/school/:schoolId",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <MsSchoolDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "ladder-time",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <LadderTimeReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "exploration-report",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <ExplorationReportLayout />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <ExplorationReport />,
              },
              {
                path: "school/:schoolId",
                element: <ExplorationSchoolView />,
              },
            ],
          },
          {
            // Drilldown is filter state, not routing, so there are no children.
            path: "ms-program-milestones",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <MsProgramMilestones />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Groups
      {
        path: "groups",
        element: <Outlet />,
        children: [
          { element: <CoachGroups />, index: true },
          { path: ":id", element: <CoachGroup /> },
          {
            path: ":id/climber/:climberId",
            element: <RedirectToClimberDetail />,
          },
          { path: ":id/career-climb/:climbID", element: <CareerClimbReport /> },
        ],
      },
      // Worksites (Teachers only, WF/WBL only, or districtadmins viewing teacher data)
      {
        path: "staff/worksites",
        element: (
          <ProtectedRoute
            requiredRole={["teacher", "districtadmin"]}
            requiredLevel={["hs"]}
          >
            <WorksitesPage />
          </ProtectedRoute>
        ),
      },
      // User Management
      {
        path: "users/coaches",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <ManageCoaches />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/staff",
        element: (
          // District admins only. Hiding the nav link was never enough — a
          // school staff member could open this directly.
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <ManageStaff />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/admins",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <ManageAdmins />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/climbers",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <ClimberSearch />
          </ProtectedRoute>
        ),
      },
      {
        path: "roster",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <RosterUsers />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/climber/:climberId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <ClimberDetail />
          </ProtectedRoute>
        ),
      },
      // Sites
      {
        path: "sites",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <Sites />
          </ProtectedRoute>
        ),
      },
      {
        path: "sites/school/:schoolID",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <SchoolGroups />
          </ProtectedRoute>
        ),
      },
      // Educator Downloads
      {
        path: "educator-downloads",
        element: <EducatorDownloads />,
      },
      {
        path: "educator-downloads/lessons",
        element: <EducatorDownloads resourceToDisplay="Lessons" />,
      },
      {
        path: "educator-downloads/implementation-materials",
        element: (
          <EducatorDownloads resourceToDisplay="Implementation Materials" />
        ),
      },
      // Climber Experience
      {
        path: "climber-experience/courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          { path: "career-climbs/:id", element: <ClimberCareerClimbItem /> },
        ],
      },
      {
        path: "climber-experience/career-climbs",
        element: <CoachCareerClimbs />,
      },
      {
        path: "climber-experience/career-climbs/:id",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <CoachCareerClimbItem />
          </ProtectedRoute>
        ),
      },

      {
        path: "climber-experience/courses/:id",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <CoachCareerClimbItem />
          </ProtectedRoute>
        ),
      },

      // Ladders (HS only)
      {
        path: "climber-experience/ladders",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <Suspense fallback={<HatLoading />}>
              <Outlet />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [{ element: <BrowseLadders />, index: true }],
      },
      // Hats (non-MS)
      {
        path: "climber-experience/hats",
        element: (
          <ProtectedRoute requiredLevel={["hs", "wf"]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ element: <HatsMain />, index: true }],
      },

      // Settings
      {
        path: "settings",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]} requireWrite>
            <Settings />
          </ProtectedRoute>
        ),
      },
      // Course Plan Configuration
      {
        path: "settings/courses",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]} requireWrite>
            <CoursePlanConfig />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // -------- Climbers for Teachers (HS only) ----------
    element: (
      <ProtectedRoute requiredRole={["teacher"]} requiredLevel={["hs"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "climbers",
        element: <Outlet />,
        children: [
          { element: <ClimberList />, index: true },
          { path: ":climberId", element: <RedirectToClimberDetail /> },
        ],
      },
    ],
  },
  {
    // -------- Dashboards ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      // Climber Dashboard
      {
        path: "climber/dashboard",
        element: (
          <ProtectedRoute
            requiredRole={["climber"]}
            requiredLevel={["hs", "wf"]}
          >
            <ClimberDashboard />
          </ProtectedRoute>
        ),
      },
      // Teacher Dashboard & Features
      {
        path: "teacher/dashboard",
        element: (
          <ProtectedRoute requiredRole={["teacher"]}>
            <TeacherDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "teacher/review-answers/:sessionId/:userId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
            requiredLevel={["hs", "ms"]}
          >
            <StudentAnswerReview />
          </ProtectedRoute>
        ),
      },
      // District Admin Dashboard
      // For Dadmins:
      {
        path: "districtadmin/dashboard",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <DistrictAdminDashboard />
          </ProtectedRoute>
        ),
      },

      // The district-in-the-URL variant, for the two roles that have no
      // district of their own and enter one: super admins and Hats staff.
      // Both reach it after setActiveDistrict, and both are district admins
      // for that district by the time the guard runs — but the guard matches
      // on the ACTUAL role as well as the effective one, so each has to be
      // named here or it 404s with the district already set.
      {
        path: "districtadmin/dashboard/:districtId",
        element: (
          <ProtectedRoute requiredRole={["superadmin", "hatsstaff"]}>
            <DistrictAdminDashboard />
          </ProtectedRoute>
        ),
      },
      // School Staff Dashboard
      {
        path: "schoolstaff/dashboard",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff"]}>
            <SchoolStaffDashboard />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // -------- Super Admin Dashboard (no sidebar) ----------
  {
    element: (
      <ProtectedRoute requiredRole={["superadmin"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "superadmin/dashboard",
        element: <SuperAdminDashboard />,
      },
    ],
  },

  {
    // -------- login ----------
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "",
        children: [{ element: <Login />, index: true }],
      },
      {
        path: "post-login",
        children: [{ element: <PostLogin />, index: true }],
      },
    ],
  },
  // Picks the MS or the WF 404 itself — see NotFoundRoute.
  { path: "404", element: <NotFoundRoute /> },
  // Must stay last: a mistyped or stale URL matched nothing at all and rendered
  // an empty document, which looks like an outage rather than a bad link.
  { path: "*", element: <Navigate to="/404" replace /> },
];
